import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

const LOGO_SRC = '/images/hxd-logo-header.png';

// Subscribers reach /referrals via the SubscriberSubNav lower bar — no need
// to duplicate it in the top nav. Top nav now only carries editor/dev tools.
const adminLinks = [
  { to: '/admin/editor', label: 'Editor' },
  { to: '/admin', label: 'Admin' },
];

const devLinks = [
  { to: '/admin/dev', label: 'Dev' },
];

function navClass({ isActive }) {
  return ['hxd-nav-btn', isActive ? 'active' : ''].filter(Boolean).join(' ');
}

function isAdminHubPath(pathname) {
  return (
    pathname.startsWith('/admin') &&
    !pathname.startsWith('/admin/dev') &&
    !pathname.startsWith('/admin/editor')
  );
}

function RoleBadge({ role }) {
  if (!role) return null;
  const label = role === 'dev' ? 'Dev' : role === 'editor' ? 'Editor' : 'Subscriber';
  return <span className="hxd-nav-role">{label}</span>;
}

export default function TopNav() {
  const { user, isAuthenticated, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isPrivileged = user?.role === 'editor' || user?.role === 'dev';
  const isDev = user?.role === 'dev';

  const links = [
    ...(isPrivileged ? adminLinks : []),
    ...(isDev ? devLinks : []),
  ];

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
    navigate('/');
  };

  return (
    <div className="hxd-topnav">
      <div className="hxd-topnav-inner">
        <Link to="/" className="hxd-topnav-logo" title="HangarX Daily — Home">
          <img src={LOGO_SRC} alt="Hangar X Daily" width={214} height={32} />
        </Link>

        <nav className="hxd-topnav-links" aria-label="Account and tools">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/admin'}
              className={
                link.to === '/admin'
                  ? () => navClass({ isActive: isAdminHubPath(pathname) })
                  : navClass
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <RoleBadge role={user.role} />
              <button type="button" onClick={handleSignOut} className="hxd-btn-header-ghost">
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hxd-btn-header-ghost">
                Sign In
              </Link>
              <Link to="/signup" className="hxd-btn-header-solid">
                Subscribe
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-[#0A0A0A] -mr-1"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            ) : (
              <>
                <path d="M3 6h18" strokeLinecap="round" />
                <path d="M3 12h18" strokeLinecap="round" />
                <path d="M3 18h18" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div className="hxd-mobile-nav">
          <nav className="px-4 py-3 flex flex-col gap-0.5" aria-label="Mobile menu">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/admin'}
                onClick={() => setOpen(false)}
                className={
                  link.to === '/admin'
                    ? () => navClass({ isActive: isAdminHubPath(pathname) })
                    : navClass
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-2 border-t border-[#D4D4D4] flex flex-col gap-2">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center justify-between text-[#525252] text-[12px] gap-2">
                    <span className="truncate">{user?.email}</span>
                    <RoleBadge role={user?.role} />
                  </div>
                  <button type="button" onClick={handleSignOut} className="hxd-btn-header-ghost w-full">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="hxd-btn-header-ghost w-full text-center">
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    className="hxd-btn-header-solid w-full text-center"
                  >
                    Subscribe
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}
